import { Controller } from "@hotwired/stimulus"

// Drives the one-way add-products-to-shelf flow. A search result adds one
// product; a completed native scanner session adds its confirmed batch. Either
// success exits the modal to the refreshed shelf. Products already on the shelf
// are rendered as disabled rows rather than turning this add flow into an
// editor that can remove them.
export default class extends Controller {
  static targets = ["searchForm", "searchInput", "error", "exit"]
  static values = { batchPath: String }

  disconnect() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout)
  }

  handleSearchInput() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout)

    this.searchTimeout = setTimeout(() => {
      this.searchFormTarget.requestSubmit()
    }, 300)
  }

  async addProduct(event) {
    const button = event.currentTarget
    const productId = Number(button.dataset.productId)
    if (!productId || button.disabled) return

    button.disabled = true

    if (await this.submitProducts([{ product_id: productId }], "search")) {
      this.exit()
    } else {
      button.disabled = false
      button.dispatchEvent(new CustomEvent("loading-button:stop"))
    }
  }

  // Only the scanner's Done action commits. Its search fallback restores this
  // modal with status "search", leaving the user free to find a product by name
  // without silently adding the scans they had not confirmed.
  async addScannedProducts(event) {
    if (event.detail?.status !== "completed") return

    const products = Array.from(event.detail.products || [])
      .map(product => ({ product_id: Number(product.product_id || product.productId) }))
      .filter(product => product.product_id)

    if (products.length === 0) return

    if (await this.submitProducts(products, "scanner")) this.exit()
  }

  async submitProducts(products, source) {
    this.hideError()

    try {
      const response = await fetch(this.batchPathValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ shelf_products: { source, products } })
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        this.showError(payload.message || "We couldn’t add that product. Please try again.")
        return false
      }

      return true
    } catch (error) {
      console.error("Error adding products to shelf:", error)
      this.showError("We couldn’t add that product. Please try again.")
      return false
    }
  }

  exit() {
    if (this.hasExitTarget) this.exitTarget.click()
  }

  showError(message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  hideError() {
    if (!this.hasErrorTarget) return

    this.errorTarget.classList.add("hidden")
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
